import { UnorderedListOutlined } from '@ant-design/icons';
import { orderBy } from 'lodash';
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTheme } from 'styled-components';

import {
  type ResizeCorner,
  FLOATING_CARD_BELOW_MODAL_Z_INDEX,
  FloatingCardShell,
  getAnchorPosition,
  useFloatingCard,
} from 'components/FloatingCard';
import { Button } from 'components/ui';
import { useIsInView } from 'utils/hooks';

import { propagationTypeMapping } from '../../utils';
import { ColorCodingAttribute } from '../BladeDamagePlotContainer';
import { type ColorCodingMap, NullShapeComponent } from '../pointUtils';

import {
  DamageCountSpan,
  LegendContent,
  LegendScrollArea,
  LegendSymbol,
  LegendToggleWrapper,
} from './ColorCodingLegend.style';
import { getLegendInitialSize, getLegendMaxHeight, getLegendScale, LEGEND_MIN_SIZE } from './utils';

type ColorCodingLegendCardProps = {
  colorCodingAttribute: ColorCodingAttribute;
  colorCodingMap: ColorCodingMap;
  totalDamageCount: number;
  toggleRef: React.RefObject<HTMLButtonElement | null>;
  onClose: () => void;
};

const ColorCodingLegendCard = ({
  colorCodingAttribute,
  colorCodingMap,
  totalDamageCount,
  toggleRef,
  onClose,
}: ColorCodingLegendCardProps) => {
  const theme = useTheme();
  const topRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const contentMeasureRef = useRef<HTMLDivElement>(null);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const hasUserResizedHeightRef = useRef(false);
  const [enableScroll, setEnableScroll] = useState(false);
  const isScrolledToTop: boolean = useIsInView(topRef);
  const isScrolledToBottom: boolean = useIsInView(bottomRef);

  const initialSize = useMemo(() => getLegendInitialSize(colorCodingMap), [colorCodingMap]);

  const {
    position,
    size,
    setSize,
    setPosition,
    headerRef,
    isAnchorPositioned,
    onHeaderMouseDown,
    onResizeMouseDown,
    resizeCursors,
    getCornerHandleProps,
  } = useFloatingCard({
    minSize: LEGEND_MIN_SIZE,
    initialSize,
    anchorRef: toggleRef,
    anchorPlacement: 'above',
  });

  const legendScale = getLegendScale(size.width);

  const noValueDamageCount: number =
    totalDamageCount -
    Object.values(colorCodingMap).reduce((acc, { damageCount }) => acc + (damageCount ?? 0), 0);

  const handleResizeMouseDown = useCallback(
    (event: React.MouseEvent, corner: ResizeCorner) => {
      hasUserResizedHeightRef.current = true;
      setEnableScroll(true);
      onResizeMouseDown(event, corner);
    },
    [onResizeMouseDown]
  );

  const syncLegendAnchorPosition = useCallback(
    (cardWidth: number, cardHeight: number) => {
      if (hasUserResizedHeightRef.current || !toggleRef.current) {
        return;
      }

      setPosition(getAnchorPosition(toggleRef.current, cardWidth, cardHeight, 'above'));
    },
    [setPosition, toggleRef]
  );

  const applyLegendHeight = useCallback(
    (targetHeight: number) => {
      setSize(currentSize => {
        if (currentSize.height === targetHeight) {
          return currentSize;
        }

        syncLegendAnchorPosition(currentSize.width, targetHeight);
        return { ...currentSize, height: targetHeight };
      });
    },
    [setSize, syncLegendAnchorPosition]
  );

  useLayoutEffect(() => {
    if (!isAnchorPositioned || hasUserResizedHeightRef.current) {
      return;
    }

    const headerHeight = headerRef.current?.offsetHeight ?? 0;
    const contentHeight = contentMeasureRef.current?.offsetHeight ?? 0;
    if (!contentHeight) {
      return;
    }

    const maxHeight = getLegendMaxHeight();
    const naturalHeight = headerHeight + contentHeight;
    const isHeightCapped = naturalHeight > maxHeight;
    const targetHeight = Math.min(Math.max(LEGEND_MIN_SIZE.height, naturalHeight), maxHeight);

    setEnableScroll(isHeightCapped);
    applyLegendHeight(targetHeight);

    if (!isHeightCapped) {
      requestAnimationFrame(() => {
        const scrollEl = scrollAreaRef.current;
        if (!scrollEl || hasUserResizedHeightRef.current) {
          return;
        }

        const overflow = scrollEl.scrollHeight - scrollEl.clientHeight;
        if (overflow > 0) {
          setSize(currentSize => {
            const nextHeight = currentSize.height + overflow;
            syncLegendAnchorPosition(currentSize.width, nextHeight);
            return { ...currentSize, height: nextHeight };
          });
        }
      });
    }
  }, [
    applyLegendHeight,
    colorCodingMap,
    isAnchorPositioned,
    headerRef,
    legendScale,
    noValueDamageCount,
    setSize,
    syncLegendAnchorPosition,
    totalDamageCount,
  ]);

  if (!isAnchorPositioned) {
    return null;
  }

  return (
    <FloatingCardShell
      title={`${colorCodingAttribute}`}
      icon={<UnorderedListOutlined />}
      onClose={onClose}
      scale={legendScale}
      position={position}
      size={size}
      headerRef={headerRef}
      onHeaderMouseDown={onHeaderMouseDown}
      onResizeMouseDown={handleResizeMouseDown}
      resizeCursors={resizeCursors}
      getCornerHandleProps={getCornerHandleProps}
      usePortal
      zIndex={FLOATING_CARD_BELOW_MODAL_Z_INDEX}
    >
      <LegendScrollArea
        ref={scrollAreaRef}
        $enableScroll={enableScroll}
        $isScrolledToTop={isScrolledToTop}
        $isScrolledToBottom={isScrolledToBottom}
      >
        <LegendContent ref={contentMeasureRef}>
          <div ref={topRef} />
          <ul>
            {colorCodingMap &&
              orderBy(
                Object.entries(colorCodingMap),
                [0],
                [
                  [ColorCodingAttribute.Severity, ColorCodingAttribute.OpSeverity].includes(
                    colorCodingAttribute
                  )
                    ? 'desc'
                    : 'asc',
                ]
              ).map(
                (
                  [
                    value,
                    {
                      symbol: { ShapeComponent, color },
                      damageCount,
                    },
                  ],
                  i
                ) =>
                  !!damageCount && (
                    <li key={i}>
                      <LegendSymbol
                        viewBox="0 0 10 10"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <ShapeComponent
                          x={5}
                          y={5}
                          color={color}
                          opacity={theme.plot.opacity.default}
                        />
                      </LegendSymbol>
                      <span>
                        {[ColorCodingAttribute.Severity, ColorCodingAttribute.OpSeverity].includes(
                          colorCodingAttribute
                        )
                          ? colorCodingAttribute
                          : ''}{' '}
                        {colorCodingAttribute === ColorCodingAttribute.PropagationType
                          ? propagationTypeMapping[value]
                          : value}
                      </span>
                      : <DamageCountSpan>{damageCount}</DamageCountSpan>
                    </li>
                  )
              )}
            <li>
              <LegendSymbol viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                <NullShapeComponent
                  x={5}
                  y={5}
                  color={theme.plot.defaultPointColor}
                  opacity={theme.plot.opacity.default}
                />
              </LegendSymbol>
              <span> No {colorCodingAttribute}</span>:{' '}
              <DamageCountSpan>{noValueDamageCount}</DamageCountSpan>
            </li>
          </ul>
          <div ref={bottomRef} />
        </LegendContent>
      </LegendScrollArea>
    </FloatingCardShell>
  );
};

type ColorCodingLegendProps = {
  colorCodingAttribute: ColorCodingAttribute;
  colorCodingMap: ColorCodingMap;
  totalDamageCount: number;
};

export const ColorCodingLegend: React.FunctionComponent<ColorCodingLegendProps> = ({
  colorCodingAttribute,
  colorCodingMap,
  totalDamageCount,
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(true);
  const toggleRef = useRef<HTMLButtonElement>(null);

  return (
    <LegendToggleWrapper>
      <Button
        ref={toggleRef}
        icon={<UnorderedListOutlined />}
        onClick={() => setIsOpen(open => !open)}
        title={isOpen ? 'Hide legend' : 'Show legend'}
      />
      {isOpen && (
        <ColorCodingLegendCard
          key={colorCodingAttribute}
          colorCodingAttribute={colorCodingAttribute}
          colorCodingMap={colorCodingMap}
          totalDamageCount={totalDamageCount}
          toggleRef={toggleRef}
          onClose={() => setIsOpen(false)}
        />
      )}
    </LegendToggleWrapper>
  );
};
